import { ProductDetails_attributes_values } from "@the-volte/svc-core-sdk/lib/fragments/gqlTypes/ProductDetails";

const getVariantsStructuredData = (
  variants,
  productCondition: ProductDetails_attributes_values,
  isPurchaseType?: boolean
) => {
  const inStock = "https://schema.org/InStock";
  const outOfStock = "https://schema.org/OutOfStock";

  // if isPurchaseType, only include offer for purchase
  const filteredVariants =
    isPurchaseType === true
      ? variants.filter((variant) => variant.name === "Purchase")
      : variants;

  return filteredVariants.map((variant) => ({
    "@type": "Offer",
    priceCurrency: variant.pricing.price.gross.currency,
    price: (
      variant.pricing.price.gross.amount +
      variant.pricing.borrowerFee.gross.amount
    ).toFixed(2),
    itemCondition:
      productCondition?.slug === "new" ||
      productCondition?.slug === "new-with-tags"
        ? "https://schema.org/NewCondition"
        : "https://schema.org/UsedCondition",
    availability: variant.quantityAvailable > 0 ? inStock : outOfStock,
  }));
};

function getProductBrand(product) {
  const designerAttribute = product?.attributes?.find(
    ({ attribute }) => attribute?.name === "Designer"
  );
  const [designerValue] = designerAttribute?.values || [];

  return designerValue?.name || "";
}

const getSku = (variants) => {
  if (Array.isArray(variants) && variants.length > 0) {
    return variants[0].sku ?? "";
  }

  return "";
};

export const structuredData = (product, isPurchaseType?: boolean) => {
  const images = product.images.map((image) => new URL(image.url));
  const productConditionAttribute = product?.attributes?.find(
    (attr) => attr.attribute.slug === "condition"
  );
  const [productCondition] = productConditionAttribute?.values || [];

  const { variants } = product;

  let productDescription = "";
  try {
    const descriptionData = JSON.parse(product.description);

    productDescription = descriptionData?.blocks
      ?.filter((block) => block?.type === "paragraph")
      ?.map((block) => block?.data?.text?.trim())
      ?.join(". ");
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error parsing product description:", error);
  }

  productDescription =
    product?.seoDescription || productDescription || product.name;

  return JSON.stringify({
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product?.name?.trim(),
    image: images,
    description: productDescription,
    brand: {
      "@type": "Brand",
      name: getProductBrand(product),
    },
    offers: getVariantsStructuredData(
      variants,
      productCondition,
      isPurchaseType
    ),
    sku: getSku(variants),
    url: location.href,
    ...(!!product?.productAggregateRating && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: product.productAggregateRating.ratingValue,
        ratingCount: product.productAggregateRating.ratingCount,
      },
    }),
  });
};

/*
We should be getting this directly from Google, however it is suprisingly difficult.

Using the places API would be the simplest, however since we don't have a physical 
address, it doesn't work.

(Our place_id: ChIJvd_NtezkT2ERjXpVP5iszM0)

https://maps.googleapis.com/maps/api/place/details/json?key={{GOOGLE_PLACES_API_KEY}}&place_id={{PLACE_ID}}&fields=name

The result is "The provided Place ID is no longer valid" - this is just misleading error message :(
see: https://support.google.com/business/thread/120163650/the-provided-place-id-is-no-longer-valid-please-refresh-cached-place-ids-so-stuck?hl=en

So trying using the Google Business Profile API
see: https://developers.google.com/my-business/content/overview

https://mybusiness.googleapis.com/v4/accounts/102488506248101242686/locations/8026778460399333943/reviews?pageSize=1

{
    "reviews": [
      ...
    ],
    "averageRating": 5,
    "totalReviewCount": 18,
}

This can work but requires OAuth with a human user, which can't be automated.  Using a service account might work
according to https://stackoverflow.com/questions/40720692/accessing-google-my-business-api-without-login-using-service-account

Enabling DWD for the service account has security downsides and is not offically supported :(
https://stackoverflow.com/questions/66648811/google-mba-service-account-get-all-locations

...so I haven't tried the DWD approach yet


I think the next step would be just to add these as new model fields under `site_sitesettings` and add them to
the admin dashboard, so they can be manually updated each week.  We can then expose via `Query.Shop` - and move these comments to the GraphQL server code!
*/
export const structuredDataAggregateReviews = () =>
  JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Product",
    name: "The Volte Fashion Rentals",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0", // TODO: get dynamically (see above)
      reviewCount: "18", // TODO: ditto
    },
  });
